import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import CategorySelect from "./CategorySelect";

const PAGE_SIZE = 10;

const managedVendorApi = {
  list: () => axiosInstance.get("/api/superadmin/manual-vendor-csv-records/"),
  categories: () => axiosInstance.get("/api/vendors/categories/"),
  uploadCsv: (file, categoryId) => {
    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("category_id", categoryId);
    return axiosInstance.post("/api/accounts/manual-vendor-onboarding/csv/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    });
  },
};

const CloseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const LoaderIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: "mv-spin .8s linear infinite" }}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  return [];
};

const UploadIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const toastState = { setItems: null };
const pushToast = (type, title, message = "") => {
  const id = Date.now() + Math.random();
  toastState.setItems?.((items) => [...items, { id, type, title, message }]);
  setTimeout(() => toastState.setItems?.((items) => items.filter((item) => item.id !== id)), 4200);
};

const ManagedToastContainer = () => {
  const [items, setItems] = useState([]);
  toastState.setItems = setItems;
  if (!items.length) return null;

  return (
    <div style={{ position: "fixed", right: 22, bottom: 22, zIndex: 1200, display: "grid", gap: 10 }}>
      {items.map((item) => {
        const isError = item.type === "error";
        const isInfo = item.type === "info";
        const color = isError ? "#dc2626" : isInfo ? "#2563eb" : "#16a34a";
        return (
          <div key={item.id} style={{ minWidth: 280, maxWidth: 390, padding: "12px 14px", background: "#fff", borderLeft: `4px solid ${color}`, borderRadius: 9, boxShadow: "0 6px 25px rgba(15,23,42,.14)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <div>
                <strong style={{ color: "#0f172a", fontSize: 13 }}>{item.title}</strong>
                {item.message && <p style={{ margin: "3px 0 0", color: "#64748b", fontSize: 12 }}>{item.message}</p>}
              </div>
              <button onClick={() => setItems((current) => current.filter((entry) => entry.id !== item.id))} style={{ border: 0, background: "transparent", color: "#94a3b8", cursor: "pointer", padding: 0 }}><CloseIcon /></button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CsvUploadModal = ({ file, uploading, result, categories, categoriesLoading, categoryId, onCategoryChange, onFileChange, onUpload, onClose }) => {
  const rejectedRows = (result?.results || []).filter((row) => row.status === "rejected");

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(15,23,42,.48)", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 630, maxHeight: "86vh", overflowY: "auto", background: "#fff", borderRadius: 13, boxShadow: "0 22px 60px rgba(15,23,42,.25)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, padding: "18px 20px", borderBottom: "1px solid #e2e8f0" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 750, color: "#0f172a" }}>Upload vendor CSV</h2>
            <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "#64748b" }}>Required columns: company_name, email, phone_number, gstin.</p>
          </div>
          <button onClick={onClose} disabled={uploading} style={{ border: 0, width: 31, height: 31, borderRadius: 8, background: "#f1f5f9", color: "#64748b", cursor: uploading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><CloseIcon /></button>
        </div>

        <div style={{ padding: 20 }}>
          <CategorySelect
            categories={categories}
            value={categoryId}
            onChange={onCategoryChange}
            loading={categoriesLoading}
            disabled={uploading}
          />
          <label style={{ display: "block", padding: 18, border: "1px dashed #cbd5e1", borderRadius: 10, background: "#f8fafc", cursor: uploading ? "not-allowed" : "pointer" }}>
            <input type="file" accept=".csv,text/csv" onChange={onFileChange} disabled={uploading} />
            {file && <p style={{ margin: "9px 0 0", fontSize: 12.5, color: "#475569" }}>Selected: <strong>{file.name}</strong></p>}
          </label>
          <p style={{ margin: "10px 0 0", fontSize: 12, color: "#64748b", lineHeight: 1.55 }}>Every vendor in this CSV will receive the selected category and all active sub-categories under it. Plain 10-digit Indian numbers are stored with +91.</p>

          {result && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                {[["Total", result.total_rows, "#475569", "#f1f5f9"], ["Imported", result.success_count, "#16a34a", "#dcfce7"], ["Rejected", result.rejected_count, "#dc2626", "#fee2e2"]].map(([label, value, color, background]) => (
                  <div key={label} style={{ padding: "10px 12px", borderRadius: 8, background, color, textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>{value ?? 0}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
                  </div>
                ))}
              </div>

              {rejectedRows.length > 0 && (
                <div style={{ marginTop: 12, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                  <div style={{ padding: "8px 10px", background: "#fff7ed", color: "#c2410c", fontSize: 12, fontWeight: 700 }}>Rejected rows</div>
                  <div style={{ maxHeight: 180, overflowY: "auto" }}>
                    {rejectedRows.map((row) => (
                      <div key={row.row_number} style={{ padding: "9px 10px", borderTop: "1px solid #f1f5f9", fontSize: 12, color: "#475569" }}>
                        <strong>Row {row.row_number}:</strong> {row.reason || row.detail || "Rejected"}{row.email ? ` — ${row.email}` : ""}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "16px 20px", borderTop: "1px solid #e2e8f0" }}>
          <button onClick={onClose} disabled={uploading} style={{ padding: "9px 16px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, color: "#475569", fontWeight: 650, cursor: uploading ? "not-allowed" : "pointer" }}>Close</button>
          <button onClick={onUpload} disabled={!file || !categoryId || uploading} style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", background: "#3b82f6", border: 0, borderRadius: 8, color: "#fff", fontWeight: 750, cursor: !file || !categoryId || uploading ? "not-allowed" : "pointer", opacity: !file || !categoryId || uploading ? 0.65 : 1 }}>
            {uploading ? <LoaderIcon /> : <UploadIcon />}
            {uploading ? "Uploading…" : "Upload CSV"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ManagedVendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const isMounted = useRef(true);

  useEffect(() => {
    // Strict Mode replays setup after cleanup during development.
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await managedVendorApi.list();
      setVendors(extractList(data));
    } catch (error) {
      pushToast("error", "Failed to load vendors", error?.response?.data?.detail || "Unable to fetch managed vendors.");
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);
  useEffect(() => { setPage(1); }, [search]);

  const fetchCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const { data } = await managedVendorApi.categories();
      setCategories(extractList(data));
    } catch (error) {
      pushToast("error", "Failed to load categories", error?.response?.data?.detail || "Unable to fetch categories.");
    } finally {
      if (isMounted.current) setCategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (modalOpen && !categories.length) fetchCategories();
  }, [modalOpen, categories.length, fetchCategories]);

  const filteredVendors = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return vendors;
    return vendors.filter((vendor) => [
      vendor.company_name,
      vendor.business_name,
      vendor.user_name,
      vendor.email,
      vendor.user_email,
      vendor.phone_number,
      vendor.user_phone,
      vendor.gstin,
      vendor.pan,
      vendor.category_name,
    ].filter(Boolean).some((value) => String(value).toLowerCase().includes(query)));
  }, [vendors, search]);

  const totalPages = Math.max(1, Math.ceil(filteredVendors.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedVendors = filteredVendors.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleUpload = async () => {
    if (!csvFile || !categoryId || csvUploading) return;
    setCsvUploading(true);
    setCsvResult(null);
    try {
      const { data } = await managedVendorApi.uploadCsv(csvFile, categoryId);
      setCsvResult(data);
      if ((data?.rejected_count ?? 0) > 0) pushToast("info", "CSV processed", `${data?.success_count ?? 0} imported, ${data?.rejected_count ?? 0} rejected.`);
      else pushToast("success", "CSV processed", `${data?.success_count ?? 0} vendor(s) imported.`);
      if ((data?.success_count ?? 0) > 0) await fetchVendors();
    } catch (error) {
      const payload = error?.response?.data;
      const missingColumns = payload?.missing_columns;
      const detail = payload?.detail || payload?.message || error?.message || "CSV upload failed.";
      pushToast("error", "CSV upload failed", missingColumns?.length ? `${detail} Missing: ${missingColumns.join(", ")}` : detail);
    } finally {
      setCsvUploading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setCsvFile(null);
    setCsvResult(null);
    setCategoryId("");
  };

  const headerButtonBase = { display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 8, fontSize: 13.5, fontWeight: 700, cursor: "pointer", border: 0 };

  return (
    <div style={{ padding: 24, background: "#f8fafc", minHeight: "100vh", color: "#1e293b", fontFamily: "'Inter', -apple-system, sans-serif" }}>
      <style>{`@keyframes mv-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } * { box-sizing: border-box; }`}</style>
      <ManagedToastContainer />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 750, color: "#0f172a" }}>Managed Vendors</h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>View CSV-managed vendors and upload new vendor records.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setModalOpen(true)} style={{ ...headerButtonBase, background: "#3b82f6", color: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.08)" }}><UploadIcon /> Upload CSV</button>
          <button onClick={fetchVendors} style={{ ...headerButtonBase, background: "#fff", color: "#475569", border: "1px solid #e2e8f0" }}>{loading ? <LoaderIcon /> : "↻"} Refresh</button>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by company, contact, email or phone…" style={{ width: "100%", maxWidth: 430, padding: "10px 13px", border: "1px solid #e2e8f0", borderRadius: 9, background: "#fff", fontSize: 13.5, outline: "none" }} />
      </div>

      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(15,23,42,.05)" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
            <thead>
              <tr>
                {["Sr No.", "Business Name", "Contact", "GST / PAN", "Created At"].map((header) => (
                  <th key={header} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" style={{ padding: "42px 16px", textAlign: "center", color: "#64748b" }}><LoaderIcon /> Loading vendors…</td></tr>
              ) : pagedVendors.length === 0 ? (
                <tr><td colSpan="5" style={{ padding: "44px 16px", textAlign: "center", color: "#64748b" }}>No managed vendors found.</td></tr>
              ) : pagedVendors.map((vendor, index) => (
                <tr key={vendor.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "13px 16px", color: "#94a3b8" }}>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>
                  <td style={{ padding: "13px 16px", fontWeight: 700, color: "#0f172a" }}>{vendor.company_name || vendor.business_name || "—"}</td>
                  <td style={{ padding: "13px 16px", color: "#475569" }}>
                    <div>{vendor.user_name || vendor.contact_person_name || vendor.company_name || "—"}</div>
                    <div style={{ fontSize: 11.5, color: "#94a3b8" }}>{vendor.email || vendor.user_email || "—"}</div>
                    <div style={{ fontSize: 11.5, color: "#94a3b8" }}>{vendor.phone_number || vendor.user_phone || "—"}</div>
                  </td>
                  <td style={{ padding: "13px 16px", fontSize: 12, color: "#475569" }}>
                    <div>GST: {vendor.gst_status?.replace(/_/g, " ") || "—"}</div>
                    <div>PAN: {vendor.pan_status?.replace(/_/g, " ") || "—"}</div>
                  </td>
                  <td style={{ padding: "13px 16px", color: "#475569", whiteSpace: "nowrap" }}>{vendor.created_at ? new Date(vendor.created_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 16px", borderTop: "1px solid #f1f5f9", color: "#64748b", fontSize: 12.5 }}>
            <span>Page {currentPage} of {totalPages}</span>
            <div style={{ display: "flex", gap: 7 }}>
              <button onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={currentPage === 1} style={{ padding: "7px 12px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", cursor: currentPage === 1 ? "not-allowed" : "pointer", opacity: currentPage === 1 ? 0.5 : 1 }}>Previous</button>
              <button onClick={() => setPage((value) => Math.min(totalPages, value + 1))} disabled={currentPage === totalPages} style={{ padding: "7px 12px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", cursor: currentPage === totalPages ? "not-allowed" : "pointer", opacity: currentPage === totalPages ? 0.5 : 1 }}>Next</button>
            </div>
          </div>
        )}
      </div>

      {modalOpen && (
        <CsvUploadModal
          file={csvFile}
          uploading={csvUploading}
          result={csvResult}
          categories={categories}
          categoriesLoading={categoriesLoading}
          categoryId={categoryId}
          onCategoryChange={setCategoryId}
          onFileChange={(event) => { setCsvFile(event.target.files?.[0] || null); setCsvResult(null); }}
          onUpload={handleUpload}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default ManagedVendors;
